/**
 * Match confidence for a patient record returned by the Search Patient Records screen — how far the
 * submitted request and the matched EHR record agree about who this person is.
 *
 * The level is not decoration: it decides whether linking the record needs a documented override
 * justification. High links straight through; Medium and Low both route to the Identity Mismatch
 * state of the Confirm & Link modal. So it is built on the same predicates as that override gate
 * ({@link isNameMismatch} and friends) rather than a second comparison that could disagree with it.
 *
 * **Scored over fewer fields than the written spec.** The acceptance criteria describe six
 * identifying fields — first name, last name, date of birth, last-5 SSN, email and MRN. Two of them
 * cannot be scored against a search result as the API stands:
 *
 * - **SSN** exists on the Bolt patient record but is not returned by `Bolt/Patients/Search`
 *   (`BoltPatientDto` carries no SSN), so there is nothing to compare the submitted last-5 against.
 * - **Email** does not exist on the Bolt patient record at all — the email on a request is the
 *   requester's portal-account address, which has no EHR counterpart to agree or disagree with.
 *
 * Scoring either one would mean inventing agreement the data cannot support, on the comparison that
 * decides whether a staff member has to justify a link. So the structure of the spec is kept — the
 * name/DOB baseline is the gate, a corroborating identifier is what lifts a match to High — over
 * the fields that actually exist. If the search response gains a last-5 SSN, it belongs here as a
 * second corroborating identifier.
 */

import { RequestDetailPatient } from '../models/request-detail';
import {
  isDateOfBirthMismatch,
  isMedicalRecordNumberMismatch,
  isNameMismatch,
} from './identity-match.utils';

/** How far a record agrees with the submitted request. */
export type MatchConfidence = 'High' | 'Medium' | 'Low';

/** The identifying fields a request submits, as the confidence comparison reads them. */
export interface SubmittedIdentity {
  /** Null for a request that submitted no name — never a placeholder. See {@link isNameMismatch}. */
  patientDisplayName: string | null;
  dateOfBirth: string;
  medicalRecordNumber: string | null;
}

/**
 * The chip per level. Colours come from the global `.badge--*` utilities the request queues already
 * use, so a staff member reads the same three colours here as everywhere else: green for a clean
 * result, amber for one that needs a look, neutral grey for one the screen cannot vouch for.
 *
 * Low is grey rather than red on purpose. Red is this app's "denied / revoked" treatment — a
 * decision someone made — and a weak match is not a decision, it is a record nobody has verified.
 */
export const MATCH_CONFIDENCE_BADGES = {
  High: { label: 'High', class: 'badge--approved' },
  Medium: { label: 'Medium', class: 'badge--needs-info' },
  Low: { label: 'Low', class: 'badge--unknown' },
} as const satisfies Record<MatchConfidence, { label: string; class: string }>;

/**
 * How far the submitted request and one matched record agree.
 *
 * - **Low** — the name/DOB baseline is not fully met. Whatever else lines up, nobody has confirmed
 *   this is the same person, and that is the state with the highest risk of linking the wrong
 *   patient. A blank submitted name lands here too: two unknowns are not evidence of agreement.
 * - **Medium** — the baseline matches, but a submitted MRN fails to corroborate it. The request
 *   named an identifier this record does not answer to, which is worth a staff member's attention
 *   before the link is made.
 * - **High** — the baseline matches and nothing contradicts it: either the submitted MRN agrees, or
 *   no MRN was submitted and there is nothing further to check. (The spec excludes an unsubmitted
 *   MRN from the comparison rather than counting it against the record, which is what keeps an
 *   ordinary name + DOB match out of the override flow.)
 */
export function getMatchConfidence(
  submitted: SubmittedIdentity,
  patient: RequestDetailPatient,
): MatchConfidence {
  const baselineMatches =
    !isNameMismatch(submitted.patientDisplayName, patient.displayName) &&
    !isDateOfBirthMismatch(submitted.dateOfBirth, patient.dateOfBirth);

  if (!baselineMatches) {
    return 'Low';
  }

  return isMedicalRecordNumberMismatch(submitted.medicalRecordNumber, patient.medicalRecordNumber)
    ? 'Medium'
    : 'High';
}

/** The chip for a level. */
export function getMatchConfidenceBadge(level: MatchConfidence): { label: string; class: string } {
  return MATCH_CONFIDENCE_BADGES[level];
}

/** Whether linking this record should be treated as an identity override. */
export function requiresIdentityOverride(level: MatchConfidence): boolean {
  return level !== 'High';
}

/**
 * The one record to draw the eye to, or null when there isn't one. Takes the levels already worked
 * out per patient id, so a caller that also renders each row's chip scores every record once.
 *
 * Only a **High** record leads. A Medium one opens the Confirm & Link modal as "Identity Mismatch
 * Detected", and highlighting it as the closest match would send staff into that warning on the
 * screen's own recommendation. Null when **several** records are High, too: singling one of several
 * equally-good matches out is an endorsement the data does not support, and on a screen where the
 * next click links a patient record for good, a highlight that means "first in an arbitrary sort
 * order" is worse than no highlight at all.
 */
export function getLeadingMatchPatientId(
  confidences: ReadonlyMap<number, MatchConfidence>,
): number | null {
  let leader: number | null = null;

  for (const [patientId, level] of confidences) {
    if (level !== 'High') {
      continue;
    }
    if (leader !== null) {
      return null;
    }
    leader = patientId;
  }

  return leader;
}
